import React, { useState, useRef } from "react";
import { DialogRoot } from "components/ui/dialog";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@chakra-ui/react";
import { Button } from "components/ui/button";

import { toast } from "react-toastify";
import { handleCreateAds } from "actions/actions";
import AdsFilter from "./AdsFilter";

const adRoles = [
  { value: "user", label: "العملاء" },
  { value: "contractor", label: "المقاولون" },
  { value: "supplier", label: "الموردون" },
];

const CreateAds = ({ refetch }) => {
  const [photo, setPhoto] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [targetAudiences, setTargetAudiences] = useState([]);
  const [preview, setPreview] = useState(null);
  const closeRef = useRef();

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const toggleTargetAudience = (role) => {
    setTargetAudiences((current) =>
      current.includes(role)
        ? current.filter((item) => item !== role)
        : [...current, role]
    );
  };

  const handleSend = async () => {
    if (!photo) {
      toast.error("يرجى اختيار صورة قبل الإرسال.");
      return;
    }

    const formData = new FormData();
    formData.append("photo", photo);
    if (startDate) formData.append("startDate", startDate);
    if (endDate) formData.append("endDate", endDate);
    formData.append("targetAudiences", JSON.stringify(targetAudiences));
    formData.append(
      "targetAudience",
      targetAudiences.length === 0 || targetAudiences.length === adRoles.length
        ? "all"
        : targetAudiences[0]
    );
    // add date utc
    formData.append("createdAt", new Date().toISOString());

    const res = await handleCreateAds(formData);

    if (res.error) {
      return toast.error(res.error);
    } else {
      toast.success(res.success);
      closeRef.current.click();
      setPhoto(null);
      setPreview(null);
      setStartDate("");
      setEndDate("");
      setTargetAudiences([]);
      refetch();
    }
  };

  return (
    <>
      <AdsFilter />
      <DialogRoot dir="rtl">
        <DialogTrigger asChild>
          <button
            className="my-4 rounded-md bg-blueSecondary py-2 px-4 text-white transition-all float-end hover:bg-brandLinear"
            variant="outline"
            size="sm"
          >
            اضافة اعلان
          </button>
        </DialogTrigger>
        <DialogContent className="fixed top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white text-gray-700 end-1/2 dark:bg-navy-800 dark:text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              إضافة اعلان جديد
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {/* Photo input with preview */}
            <div className="flex flex-col items-center justify-center">
              <div className="flex h-48 w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300">
                <input
                  type="file"
                  id="photo"
                  className="hidden"
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
                <label htmlFor="photo" className="cursor-pointer text-gray-400">
                  {preview ? (
                    <img
                      src={preview}
                      alt="Preview"
                      className="h-44 object-contain"
                    />
                  ) : (
                    <span>اضغط لاختيار صورة</span>
                  )}
                </label>
              </div>
            </div>
            {/* start date (optional) */}
            <div className="mt-4">
              <label
                htmlFor="startDate"
                className="mb-2 block text-sm text-gray-800 dark:text-white"
              >
                تاريخ البداية (اختياري)
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="focus:ring-primary-500 w-full rounded border border-gray-300 bg-white p-2 text-gray-800 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
              />
            </div>

            {/* end date (optional) */}
            <div className="mt-4">
              <label
                htmlFor="endDate"
                className="mb-2 block text-sm text-gray-800 dark:text-white"
              >
                تاريخ النهاية (اختياري)
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="focus:ring-primary-500 w-full rounded border border-gray-300 bg-white p-2 text-gray-800 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
              />
            </div>

            {/* target audience */}
            <div className="mt-4">
              <p className="mb-2 block text-sm text-gray-800 dark:text-white">
                يظهر الإعلان لـ
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {adRoles.map((role) => (
                  <label
                    key={role.value}
                    className="flex cursor-pointer items-center gap-2 rounded border border-gray-300 bg-white p-2 text-sm text-gray-800 dark:border-gray-600 dark:bg-navy-800 dark:text-white"
                  >
                    <input
                      type="checkbox"
                      checked={targetAudiences.includes(role.value)}
                      onChange={() => toggleTargetAudience(role.value)}
                    />
                    <span>{role.label}</span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-300">
                اتركها بدون تحديد لعرض الإعلان للجميع.
              </p>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button onClick={handleSend} variant="outline">
              <span>اضافة</span>
            </Button>
            <DialogActionTrigger asChild ref={closeRef}>
              <Button variant="outline">
                <span>إلغاء</span>
              </Button>
            </DialogActionTrigger>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  );
};

export default CreateAds;
